// #2762: the settings screen shows the principal's AI work settings as
// business tasks (メールの返信, 日程調整, 請求書…). A card is a view over the
// stored `preferences.ai_work`: each step of a card reads and writes an
// exception for its verb, the card's services are the habits for its object,
// and each declared tool may carry the principal's own choice. Nothing new is
// stored for a starter card; an added card is kept in `ai_work.cards`.

import type {
  AiWorkDoer,
  AiWorkDraft,
  AiWorkExceptionDraft,
  AiWorkToolDraft,
  AiWorkVerbView,
} from './ai-work-draft-logic';

/** One state for every step and tool: an AI on its own, an AI after the principal approves, or the principal. */
export type WorkStepState = 'auto' | 'check' | 'principal';

/** The business preferences a card holds, kept in their existing profile fields. */
export type WorkCardPrefs = 'mail' | 'chat' | 'schedule' | 'payments' | 'invoices' | 'expenses';

export interface WorkCardStep {
  how: string;
  /** i18n key; an added card names the verb itself. */
  labelKey: string;
}

export interface WorkCard {
  id: string;
  /** The object word: the habit key for services and, for `what` cards, the exception's `what`. */
  what: string;
  icon: string;
  /** i18n key; null for a card the principal added (its name is `what`). */
  nameKey: string | null;
  /**
   * `service`: a step is decided per service the card uses (送る in Gmail).
   * `what`: a step is decided for the object wherever it is made (見積書を作る).
   * A `service` card with no service yet falls back to `what`.
   */
  scope: 'service' | 'what';
  steps: WorkCardStep[];
  prefs: WorkCardPrefs | null;
  added: boolean;
  /**
   * The card's services are the websites the work happens on (e-Tax), not the
   * tool an AI uses there. Any AI tool declared for the card's steps shows,
   * whatever service it names (a browser), because the browser is the only way in.
   */
  sites?: true;
}

const step = (how: string, labelKey: string): WorkCardStep => ({ how, labelKey });

/**
 * The cards every principal sees. `what` matches the words the profile used
 * before stage 4 (メール, チャット, 予定, 入金・支払い, 請求書, 経費精算), so
 * services carried over from those fields land on the right card.
 */
export const STARTER_WORK_CARDS: readonly WorkCard[] = [
  { id: 'mail', what: 'メール', icon: 'mail', nameKey: 'workCard_mail', scope: 'service', prefs: 'mail', added: false,
    steps: [step('read', 'workStep_mail_read'), step('draft', 'workStep_mail_draft'), step('send', 'workStep_mail_send')] },
  { id: 'schedule', what: '予定', icon: 'calendar_month', nameKey: 'workCard_schedule', scope: 'service', prefs: 'schedule', added: false,
    steps: [
      step('read', 'workStep_schedule_read'), step('send', 'workStep_schedule_send'),
      step('schedule', 'workStep_schedule_schedule'), step('respond_invite', 'workStep_schedule_respond'),
    ] },
  { id: 'chat', what: 'チャット', icon: 'forum', nameKey: 'workCard_chat', scope: 'service', prefs: 'chat', added: false,
    steps: [step('read', 'workStep_chat_read'), step('draft', 'workStep_chat_draft'), step('send', 'workStep_chat_send')] },
  { id: 'search', what: '資料', icon: 'search', nameKey: 'workCard_search', scope: 'service', prefs: null, added: false,
    steps: [step('read', 'workStep_search_read'), step('make', 'workStep_search_make')] },
  { id: 'quote', what: '見積書', icon: 'request_quote', nameKey: 'workCard_quote', scope: 'what', prefs: null, added: false,
    steps: [step('make', 'workStep_doc_make'), step('send', 'workStep_doc_send')] },
  { id: 'invoice', what: '請求書', icon: 'receipt', nameKey: 'workCard_invoice', scope: 'what', prefs: 'invoices', added: false,
    steps: [step('make', 'workStep_doc_make'), step('send', 'workStep_doc_send')] },
  { id: 'expense', what: '経費精算', icon: 'receipt_long', nameKey: 'workCard_expense', scope: 'what', prefs: 'expenses', added: false,
    steps: [step('apply', 'workStep_expense_apply')] },
  { id: 'approval', what: '申請', icon: 'approval', nameKey: 'workCard_approval', scope: 'what', prefs: null, added: false,
    steps: [step('read', 'workStep_approval_read'), step('approve', 'workStep_approval_approve')] },
  { id: 'payment', what: '入金・支払い', icon: 'payments', nameKey: 'workCard_payment', scope: 'what', prefs: 'payments', added: false,
    steps: [step('pay', 'workStep_payment_pay')] },
  { id: 'files', what: 'ファイル', icon: 'folder_open', nameKey: 'workCard_files', scope: 'service', prefs: null, added: false,
    steps: [step('organize', 'workStep_files_organize'), step('delete', 'workStep_files_delete')] },
  { id: 'site', what: '手続き', icon: 'language', nameKey: 'workCard_site', scope: 'service', prefs: null, added: false, sites: true,
    steps: [step('site_procedure', 'workStep_site_procedure')] },
];

/** Verbs an added card can be made of, in the order they are offered. */
export const ADDABLE_CARD_VERBS = [
  'read', 'draft', 'make', 'send', 'register', 'apply', 'update', 'organize', 'delete', 'approve', 'site_procedure', 'schedule', 'respond_invite', 'pay',
] as const;

export const ADDED_CARDS_MAX = 30;

/** Starter cards, then the ones the principal added. An added card with a starter's object is not shown twice. */
export function workCards(draft: AiWorkDraft): WorkCard[] {
  const starterWhats = new Set(STARTER_WORK_CARDS.map((card) => card.what));
  const added = draft.cards
    .filter((card) => !starterWhats.has(card.what))
    .map((card): WorkCard => ({
      id: `added:${card.what}`,
      what: card.what,
      icon: 'work',
      nameKey: null,
      scope: 'what',
      steps: card.steps.map((how) => step(how, `aiWorkVerb_${how}`)),
      prefs: null,
      added: true,
    }));
  return [...STARTER_WORK_CARDS, ...added];
}

/** Same token the server stores (`normalizeGrmaWorkService`), so a typed name matches a declared one. */
export function normalizeWorkService(value: string): string | null {
  const token = value.trim().toLowerCase().replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '').replace(/_+/g, '_').replace(/^_|_$/g, '');
  return token ? token.slice(0, 40) : null;
}

export function cardServices(card: WorkCard, draft: AiWorkDraft): string[] {
  return [...new Set(draft.habits.filter((habit) => habit.what === card.what).map((habit) => habit.where))];
}

interface StepTarget {
  where: string | null;
  what: string | null;
}

function cardTargets(card: WorkCard, draft: AiWorkDraft): StepTarget[] {
  const services = cardServices(card, draft);
  if (card.scope === 'service' && services.length > 0) return services.map((where) => ({ where, what: null }));
  return [{ where: null, what: card.what }];
}

function exceptionMatches(row: AiWorkExceptionDraft, how: string, target: StepTarget): boolean {
  if (row.how !== how) return false;
  if (row.where && row.where !== target.where) return false;
  if (row.what && !(target.what && target.what.toLowerCase().includes(row.what.toLowerCase()))) return false;
  return true;
}

function specificity(row: AiWorkExceptionDraft): number {
  return (row.what ? 2 : 0) + (row.where ? 1 : 0);
}

/**
 * The same reading as the server's `resolveAiWork` (src/lib/ai-work-settings.ts)
 * for one target, before an AI says anything about the tool. The server stays
 * the authority; a test keeps the two in step.
 */
export function resolveWorkState(
  draft: AiWorkDraft,
  views: readonly AiWorkVerbView[],
  how: string,
  target: StepTarget,
  /** The tool was declared irreversible or visible to others: checked unless a row says otherwise. */
  flagged = false,
): WorkStepState {
  const view = views.find((row) => row.how === how);
  const matches = draft.exceptions
    .filter((row) => exceptionMatches(row, how, target))
    .sort((a, b) => specificity(b) - specificity(a));
  const doer: AiWorkDoer = matches.find((row) => row.doer)?.doer ?? draft.verbs[how] ?? view?.default_doer ?? 'principal';
  if (doer === 'principal') return 'principal';
  if (view?.content_check_fixed) return 'check';
  const check = matches.find((row) => typeof row.content_check === 'boolean')?.content_check ?? (view?.content_check_default === true || flagged);
  return check ? 'check' : 'auto';
}

function sameTarget(row: AiWorkExceptionDraft, how: string, target: StepTarget): boolean {
  return row.how === how && (row.where ?? null) === target.where && (row.what ?? null) === target.what;
}

function stateAt(draft: AiWorkDraft, views: readonly AiWorkVerbView[], how: string, target: StepTarget): WorkStepState {
  return resolveWorkState(draft, views, how, target);
}

/** Writes one state for one target, leaving no row when the broader settings already give it. */
function withStateAt(
  draft: AiWorkDraft,
  views: readonly AiWorkVerbView[],
  how: string,
  target: StepTarget,
  state: WorkStepState,
): AiWorkDraft {
  const view = views.find((row) => row.how === how);
  const wanted: WorkStepState = state === 'auto' && view?.content_check_fixed ? 'check' : state;
  const exceptions = draft.exceptions.filter((row) => !sameTarget(row, how, target));
  const without = { ...draft, exceptions };
  const base = stateAt(without, views, how, target);
  if (base === wanted) return without;
  const row: AiWorkExceptionDraft = { how, where: target.where, what: target.what };
  if (wanted === 'principal') {
    row.doer = 'principal';
  } else {
    if (base === 'principal') row.doer = 'ai';
    const baseWithAi = base === 'principal'
      ? stateAt({ ...without, exceptions: [...exceptions, { ...row }] }, views, how, target)
      : base;
    if (baseWithAi !== wanted) row.content_check = wanted === 'check';
  }
  return { ...without, exceptions: [...exceptions, row] };
}

/** The state a card's step shows: its first service, or its object when it has none. */
export function stepState(card: WorkCard, how: string, draft: AiWorkDraft, views: readonly AiWorkVerbView[]): WorkStepState {
  return stateAt(draft, views, how, cardTargets(card, draft)[0]);
}

/** True when the card's services disagree for this step, so the card shows the first and says so. */
export function stepStateMixed(card: WorkCard, how: string, draft: AiWorkDraft, views: readonly AiWorkVerbView[]): boolean {
  const states = new Set(cardTargets(card, draft).map((target) => stateAt(draft, views, how, target)));
  return states.size > 1;
}

export function withStepState(
  card: WorkCard,
  how: string,
  state: WorkStepState,
  draft: AiWorkDraft,
  views: readonly AiWorkVerbView[],
): AiWorkDraft {
  return cardTargets(card, draft).reduce((next, target) => withStateAt(next, views, how, target, state), draft);
}

/** Pressing a step cycles AIが自動で → 承認してからAI → ご自身で. Paying never offers the first. */
export function nextStepState(state: WorkStepState, contentCheckFixed: boolean): WorkStepState {
  if (contentCheckFixed) return state === 'principal' ? 'check' : 'principal';
  return state === 'auto' ? 'check' : state === 'check' ? 'principal' : 'auto';
}

/**
 * Changes a card's services and keeps what each step showed: a new service
 * starts with the card's current state, and the object-level rows a
 * `service` card used before its first service give way to per-service rows
 * (and come back when the last service goes).
 */
function withServices(
  card: WorkCard,
  draft: AiWorkDraft,
  views: readonly AiWorkVerbView[],
  habits: AiWorkDraft['habits'],
): AiWorkDraft {
  const before = card.steps.map((s) => ({ how: s.how, state: stepState(card, s.how, draft, views) }));
  const oldTargets = cardTargets(card, draft);
  let next: AiWorkDraft = { ...draft, habits };
  const newTargets = cardTargets(card, next);
  const dropped = oldTargets.filter((old) => old.what !== null && !newTargets.some((t) => t.what === old.what));
  if (dropped.length > 0) {
    next = {
      ...next,
      exceptions: next.exceptions.filter((row) => !dropped.some((target) => card.steps.some((s) => sameTarget(row, s.how, target)))),
    };
  }
  for (const { how, state } of before) next = withStepState(card, how, state, next, views);
  return next;
}

export function withCardServiceAdded(
  card: WorkCard,
  service: string,
  draft: AiWorkDraft,
  views: readonly AiWorkVerbView[],
): AiWorkDraft {
  const where = normalizeWorkService(service);
  if (!where || cardServices(card, draft).includes(where)) return draft;
  return withServices(card, draft, views, [...draft.habits, { what: card.what, where }]);
}

/**
 * Removing a service from a card keeps that service's rows: another card may
 * use the same service and verb (Gmail in メールの返信 and 日程調整).
 */
export function withCardServiceRemoved(
  card: WorkCard,
  service: string,
  draft: AiWorkDraft,
  views: readonly AiWorkVerbView[],
): AiWorkDraft {
  return withServices(card, draft, views, draft.habits.filter((habit) => !(habit.what === card.what && habit.where === service)));
}

export function withCardAdded(draft: AiWorkDraft, what: string, steps: readonly string[]): AiWorkDraft {
  const name = what.trim().slice(0, 40);
  const verbs = [...new Set(steps)].filter((how) => (ADDABLE_CARD_VERBS as readonly string[]).includes(how));
  if (!name || verbs.length === 0 || draft.cards.length >= ADDED_CARDS_MAX) return draft;
  if (workCards(draft).some((card) => card.what === name)) return draft;
  return { ...draft, cards: [...draft.cards, { what: name, steps: verbs }] };
}

/** Removes an added card with its own rows and services. */
export function withCardRemoved(card: WorkCard, draft: AiWorkDraft): AiWorkDraft {
  if (!card.added) return draft;
  return {
    ...draft,
    cards: draft.cards.filter((row) => row.what !== card.what),
    habits: draft.habits.filter((habit) => habit.what !== card.what),
    exceptions: draft.exceptions.filter((row) => row.what !== card.what),
  };
}

// ---- Declared tools ----------------------------------------------------

export interface DeclaredTool {
  how: string;
  service: string;
  tool_name: string;
  irreversible: boolean;
  visible_to_others: boolean;
  /**
   * Set by `cardToolsFor` when the tool reaches a card's service through
   * another one: a browser tool doing the work in Messenger.
   */
  for_service?: string;
}

export interface DeclaredAi {
  client_id: string;
  client_name: string | null;
  declared_at: string;
  expires_at: string;
  tools: DeclaredTool[];
}

const BROWSER_SERVICE = 'browser';

/**
 * Whether a tool declared for `declared` reaches the service `where`: the
 * same service, or a family name such as `freee` for `freee_invoice`. Same
 * rule as `declaredServiceCovers` in `src/lib/ai-work-routing.ts`.
 */
export function declaredServiceCovers(declared: string, where: string): boolean {
  return declared.length > 0 && (declared === where || where.startsWith(`${declared}_`));
}

/**
 * The tools of one AI that do this card's steps in this card's services.
 * A card's service is where the work lands (Messenger), not how the AI gets
 * there: the AI's own tools for that service come first, and a service it
 * declared nothing for is reached with its browser tools. A website card
 * always works through the browser.
 */
export function cardToolsFor(card: WorkCard, ai: DeclaredAi, draft: AiWorkDraft): DeclaredTool[] {
  const hows = new Set(card.steps.map((s) => s.how));
  const serviceOf = (tool: DeclaredTool) => normalizeWorkService(tool.service) ?? '';
  const browserTools = ai.tools.filter((tool) => hows.has(tool.how) && serviceOf(tool) === BROWSER_SERVICE);
  if (card.sites) return browserTools;
  return cardServices(card, draft).flatMap((service) => {
    const covers = (tool: DeclaredTool) => declaredServiceCovers(serviceOf(tool), service);
    if (ai.tools.some(covers)) {
      return ai.tools.filter((tool) => hows.has(tool.how) && covers(tool));
    }
    return browserTools.map((tool) => ({ ...tool, for_service: service }));
  });
}

/** The card service a tool's row is grouped under. */
export function toolGroupService(tool: DeclaredTool): string {
  return tool.for_service ?? normalizeWorkService(tool.service) ?? tool.service;
}

/** The AIs that declared a tool for this card. */
export function cardAis(card: WorkCard, ais: readonly DeclaredAi[], draft: AiWorkDraft): DeclaredAi[] {
  return ais.filter((ai) => cardToolsFor(card, ai, draft).length > 0);
}

function overrideFor(draft: AiWorkDraft, clientId: string, toolName: string): AiWorkToolDraft | undefined {
  return draft.tools.find((row) => row.client_id === clientId && row.tool_name === toolName);
}

function stateFromOverride(row: AiWorkToolDraft, contentCheckFixed: boolean): WorkStepState {
  if (row.doer === 'principal') return 'principal';
  return row.content_check || contentCheckFixed ? 'check' : 'auto';
}

/** What the card's step gives this tool before the principal chooses for the tool itself. */
function toolBaseState(tool: DeclaredTool, draft: AiWorkDraft, views: readonly AiWorkVerbView[]): WorkStepState {
  return resolveWorkState(draft, views, tool.how, { where: toolGroupService(tool), what: null }, tool.irreversible || tool.visible_to_others);
}

export function toolState(ai: DeclaredAi, tool: DeclaredTool, draft: AiWorkDraft, views: readonly AiWorkVerbView[]): WorkStepState {
  const row = overrideFor(draft, ai.client_id, tool.tool_name);
  const fixed = views.find((view) => view.how === tool.how)?.content_check_fixed === true;
  return row ? stateFromOverride(row, fixed) : toolBaseState(tool, draft, views);
}

/** True when the principal chose for this tool itself, so the screen offers 元に戻す. */
export function toolHasOwnState(ai: DeclaredAi, tool: DeclaredTool, draft: AiWorkDraft): boolean {
  return overrideFor(draft, ai.client_id, tool.tool_name) !== undefined;
}

/**
 * Keeps the principal's choice by AI and tool name, so it stays when the AI
 * declares again. A choice equal to what the step gives leaves no row.
 */
export function withToolState(
  ai: DeclaredAi,
  tool: DeclaredTool,
  state: WorkStepState,
  draft: AiWorkDraft,
  views: readonly AiWorkVerbView[],
): AiWorkDraft {
  const others = draft.tools.filter((row) => !(row.client_id === ai.client_id && row.tool_name === tool.tool_name));
  const fixed = views.find((view) => view.how === tool.how)?.content_check_fixed === true;
  const wanted: WorkStepState = state === 'auto' && fixed ? 'check' : state;
  if (wanted === toolBaseState(tool, draft, views)) return { ...draft, tools: others };
  const row: AiWorkToolDraft = wanted === 'principal'
    ? { client_id: ai.client_id, tool_name: tool.tool_name, doer: 'principal' }
    : { client_id: ai.client_id, tool_name: tool.tool_name, doer: 'ai', content_check: wanted === 'check' };
  return { ...draft, tools: [...others, row] };
}

export function withToolReset(ai: DeclaredAi, tool: DeclaredTool, draft: AiWorkDraft): AiWorkDraft {
  return { ...draft, tools: draft.tools.filter((row) => !(row.client_id === ai.client_id && row.tool_name === tool.tool_name)) };
}

/** 「まとめて」: one state for every tool of one AI in one service. */
export function withServiceToolsState(
  ai: DeclaredAi,
  tools: readonly DeclaredTool[],
  state: WorkStepState,
  draft: AiWorkDraft,
  views: readonly AiWorkVerbView[],
): AiWorkDraft {
  return tools.reduce((next, tool) => withToolState(ai, tool, state, next, views), draft);
}

/** The shared state of a group of tools, or null when they differ. */
export function groupState(
  ai: DeclaredAi,
  tools: readonly DeclaredTool[],
  draft: AiWorkDraft,
  views: readonly AiWorkVerbView[],
): WorkStepState | null {
  const states = new Set(tools.map((tool) => toolState(ai, tool, draft, views)));
  return states.size === 1 ? [...states][0] : null;
}

// ---- Labels --------------------------------------------------------------

const SERVICE_LABELS: Record<string, { ja: string; en: string }> = {
  gmail: { ja: 'Gmail', en: 'Gmail' },
  slack: { ja: 'Slack', en: 'Slack' },
  google_calendar: { ja: 'Google カレンダー', en: 'Google Calendar' },
  google_drive: { ja: 'Google ドライブ', en: 'Google Drive' },
  google_sheets: { ja: 'Google スプレッドシート', en: 'Google Sheets' },
  notion: { ja: 'Notion', en: 'Notion' },
  freee_accounting: { ja: 'freee 会計', en: 'freee Accounting' },
  freee_invoice: { ja: 'freee 請求書', en: 'freee Invoice' },
  freee_hr: { ja: 'freee 人事労務', en: 'freee HR' },
  moneyforward: { ja: 'マネーフォワード', en: 'Money Forward' },
  excel: { ja: 'Excel', en: 'Excel' },
  outlook: { ja: 'Outlook', en: 'Outlook' },
  outlook_calendar: { ja: 'Outlook カレンダー', en: 'Outlook Calendar' },
  teams: { ja: 'Teams', en: 'Teams' },
  line_works: { ja: 'LINE WORKS', en: 'LINE WORKS' },
  jobcan: { ja: 'ジョブカン', en: 'Jobcan' },
  canva: { ja: 'Canva', en: 'Canva' },
  browser: { ja: 'ブラウザ', en: 'Browser' },
};

export const WORK_SERVICE_SUGGESTIONS = Object.keys(SERVICE_LABELS);

export function serviceLabel(service: string, lang: string): string {
  const label = SERVICE_LABELS[service];
  return label ? (lang === 'en' ? label.en : label.ja) : service;
}

/** 10/16 for the declaration date, in the principal's timezone. */
export function declaredDateLabel(declaredAt: string, timeZone: string): string {
  const date = new Date(declaredAt);
  if (Number.isNaN(date.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, month: 'numeric', day: 'numeric' }).formatToParts(date);
    const month = parts.find((p) => p.type === 'month')?.value ?? '';
    const day = parts.find((p) => p.type === 'day')?.value ?? '';
    return `${month}/${day}`;
  } catch {
    return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
  }
}

export function aiName(ai: DeclaredAi): string {
  return ai.client_name?.trim() || 'AI';
}

/** What a tool does to its object, read from its name, so two tools under one step can be told apart. */
export type ToolActionKind = 'create' | 'update' | 'delete' | 'reply' | 'forward' | 'search' | 'get';

const TOOL_ACTION_WORDS: ReadonlyArray<[ToolActionKind, readonly string[]]> = [
  ['create', ['create', 'new', 'add', 'insert']],
  ['update', ['update', 'edit', 'modify', 'patch', 'change']],
  ['delete', ['delete', 'remove', 'trash']],
  ['reply', ['reply']],
  ['forward', ['forward']],
  ['search', ['search', 'list', 'find', 'query']],
  ['get', ['get', 'read', 'fetch']],
];

export function toolActionKind(toolName: string): ToolActionKind | null {
  const bare = toolName.slice(toolName.lastIndexOf('__') + 1).replace(/^_/, '');
  const words = bare.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  for (const word of words) {
    const hit = TOOL_ACTION_WORDS.find(([, names]) => names.includes(word));
    if (hit) return hit[0];
  }
  return null;
}
